import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ToggleLeft, ToggleRight, Trash2, UserPlus } from 'lucide-react';
import { AddUserModal } from './AddUserModal';
import { TableCard } from '../../TableCard';
import { ConfirmDialog } from '../../ConfirmDialog';
import { formatDay } from '../../formatWhen';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useStableText } from '../../../../components/StableText';

// The access allowlist: every Google account authorized to sign in to NoticeIA,
// all of them admins. A row can be added by email before that account has ever
// logged in (no lastSeen yet), and removing a row revokes access. The owner row
// is seeded by the backend and cannot be removed. Rendered inside the Admin
// page, which owns the admin guard.
//
// THREE OF THEM, one per app. The demo and the sandbox are separate apps on separate
// databases and each allowlist is its own — being shown the demo is not being given
// NoticeIA, nor is being let into the sandbox, and that difference is only real if the
// three lists are. Neither has an Admin page of its own, so all three are managed from
// here: `env` picks which database the three routes talk to, and everything else on
// this page is the same list of the same kind of row.
export type UsersEnv = 'app' | 'demo' | 'sandbox';

// The heading per app, and it is the only thing on screen that says which list a row is
// in — so every env needs one, rather than an `env === 'demo' ? … : …` that quietly
// labels the third one "prod".
const TITLE_KEY: Record<UsersEnv, string> = {
  app: 'notice.users.titleProd',
  demo: 'notice.users.titleDemo',
  sandbox: 'notice.users.titleSandbox',
};

// What the server says instead of a list, and what the section shows in its place.
// `not_here` is not a failure: only prod reaches across, so on a copy two of the three
// sections say where they are managed from.
const NO_LIST: Record<string, string> = {
  unreachable: 'notice.users.envOffline',
  not_here: 'notice.users.envNotHere',
};

type NoticeAdminUser = {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  isOwner: boolean;
  disabled: boolean;
  createdAt: string | null;
  lastSeen: string | null;
};

// POST /admin/users rejections, mapped onto what the modal shows.
const ADD_ERRORS: Record<string, string> = {
  invalid_email: 'notice.users.invalidEmail',
  already_authorized: 'notice.users.duplicate',
};

export function UsersSection({ env = 'app' }: { env?: UsersEnv }) {
  const { t, language } = useLanguage();
  const st = useStableText();
  const [users, setUsers] = useState<NoticeAdminUser[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<NoticeAdminUser | null>(null);

  // Every request this section makes carries the env, and nothing else about it
  // changes. `app` is sent explicitly rather than left off: a URL that says which
  // list it is about reads the same in the network pane as it does here.
  const params = { env };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get<{ users: NoticeAdminUser[]; error?: string }>(
        '/api/notice/admin/users', { params: { env } },
      );
      setUsers(data.users || []);
      // A database that is not there yet, or another app's list asked for from a copy —
      // the server says so rather than failing, so the table says so rather than the page.
      const why = data.error ? NO_LIST[data.error] : undefined;
      setError(why ? t(why) : null);
    } catch {
      setError(t('notice.users.loadError'));
    } finally {
      setLoaded(true);
    }
  }, [t, env]);

  useEffect(() => {
    void load();
  }, [load]);

  // Returns an i18n key on failure, null once the list has been refreshed.
  const addUser = async (email: string): Promise<string | null> => {
    try {
      await axios.post('/api/notice/admin/users', { email, env });
      await load();
      return null;
    } catch (e) {
      const code = axios.isAxiosError(e) ? e.response?.data?.error : undefined;
      return ADD_ERRORS[code as string] || 'notice.users.addError';
    }
  };

  // SUSPEND / RESTORE, and only the sandbox offers it (see the row below). Optimistic
  // would be wrong here: this is an access decision, and a switch that flips and then
  // silently did not take is worse than one that waits for the server.
  const toggleUser = async (user: NoticeAdminUser) => {
    setError(null);
    try {
      await axios.patch(
        `/api/notice/admin/users/${user.id}`, { disabled: !user.disabled }, { params },
      );
      await load();
    } catch {
      setError(t('notice.users.toggleError'));
    }
  };

  const revokeUser = async (user: NoticeAdminUser) => {
    setRevoking(null);
    try {
      await axios.delete(`/api/notice/admin/users/${user.id}`, { params });
      await load();
    } catch {
      setError(t('notice.users.removeError'));
    }
  };

  return (
    <TableCard title={t(TITLE_KEY[env])} className="flex flex-col gap-6">

      {!loaded ? (
        <div className="flex justify-center py-8 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          {error && <p className="text-center text-sm text-rose-600">{error}</p>}

          {users.length === 0 ? (
            <p className="text-center text-sm text-slate-400">
              {t('notice.users.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[34rem] table-fixed text-sm">
                <thead className="bg-slate-100 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    {/* WIDTHS DECLARED, because the table is `table-fixed`. Left to size
                        itself, the columns moved every time a row changed: the SUSPENDED
                        badge widened the user column, and the second button appearing
                        widened the last one — so toggling somebody re-laid out the whole
                        card under the pointer that did it. The actions column reserves
                        room for both buttons whether or not this row draws either. */}
                    <th className="w-[46%] px-4 py-2.5">{st('notice.users.user')}</th>
                    <th className="w-[20%] px-4 py-2.5">{st('notice.users.joined')}</th>
                    <th className="w-[22%] px-4 py-2.5">{st('notice.users.lastSeen')}</th>
                    <th className="w-[12%] px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr key={u.id} className="bg-white">
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                          ) : (
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
                              {(u.name || u.email || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center justify-center gap-1.5">
                              <span className="truncate font-medium text-slate-800">
                                {u.name || st('notice.users.noName')}
                              </span>
                              {u.isOwner && (
                                // As wide as its own word — not reserving the other language's longer one.
                                <span className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                                  {t('notice.users.ownerBadge')}
                                </span>
                              )}
                              {/* Said in the row, not only in the switch at its end: a
                                  suspended account otherwise looks exactly like an active
                                  one from across the table. */}
                              {u.disabled && (
                                <span className="inline-flex shrink-0 items-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                  {st('notice.users.disabled')}
                                </span>
                              )}
                            </div>
                            <div className="truncate text-center text-xs text-slate-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {u.createdAt ? formatDay(u.createdAt, language) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {u.lastSeen ? (
                          formatDay(u.lastSeen, language)
                        ) : (
                          <span className="text-slate-400">
                            {st('notice.users.pending')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                        {/* THE SANDBOX ONLY. Prod's allowlist is the people who run the
                            app and the demo's is who is being shown it — in both, access
                            is granted or it is not. The sandbox is the one somebody else
                            works in, where "not this week" is a real answer and deleting
                            the row to say it loses when they were added and last seen. */}
                        {!u.isOwner && env === 'sandbox' && (
                          <button
                            type="button"
                            title={t(u.disabled ? 'notice.users.enable' : 'notice.users.disable')}
                            aria-label={t(u.disabled ? 'notice.users.enable' : 'notice.users.disable')}
                            aria-pressed={!u.disabled}
                            onClick={() => void toggleUser(u)}
                            className={`rounded-lg p-1.5 transition-colors ${
                              u.disabled
                                ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            {u.disabled
                              ? <ToggleLeft className="h-4 w-4" />
                              : <ToggleRight className="h-4 w-4" />}
                          </button>
                        )}
                        {!u.isOwner && (
                          <button
                            type="button"
                            title={t('notice.users.remove')}
                            aria-label={t('notice.users.remove')}
                            onClick={() => setRevoking(u)}
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* UNDER THE TABLE: the list first, then the way to add to it. */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
            >
              <UserPlus className="h-4 w-4" />
              {t('notice.users.add')}
            </button>
          </div>
        </>
      )}

      {adding && <AddUserModal onAdd={addUser} onClose={() => setAdding(false)} />}
      {revoking && (
        <ConfirmDialog
          title={t('notice.users.removeConfirm.title')}
          body={t('notice.users.removeConfirm.body').replace('{email}', revoking.email)}
          confirmLabel={t('notice.users.remove')}
          cancelLabel={t('notice.catalog.cancel')}
          onConfirm={() => void revokeUser(revoking)}
          onCancel={() => setRevoking(null)}
        />
      )}
    </TableCard>
  );
}
